// Columna derecha de «Teléfonos» (`.col-detail` del mock): hero, acción y pestañas Producto ·
// Cuotas por nivel · Ventas hoy · Similares. Lo que el catálogo no publica se dice con `NoSource`.
import type { ReactNode } from 'react';
import { PanelLeft, Plus, Star } from 'lucide-react';
import { skuSoldCents } from '@/api/services/retail-wizard/soldAmount';
import { Badge, Tabs } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { NoSource } from '@/shared/patterns/NoSource';
import { TierPill } from '@/shared/patterns/Pills';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { formatTimeMx } from '@/lib/dateHelpers';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import {
    modelLabel,
    productLabel,
    similarProducts,
    stockLabelOf,
    stockToneOf,
} from '../../catalog/lib/catalogExplorer';
import {
    OWN_SALES_ONLY_NOTE,
    RECENT_WINDOW_DAYS,
    skuSalesFacts,
} from '../../catalog/lib/catalogSales';
import type { RecentSale } from '../../api/usePortal';
import {
    availableColors,
    financierChips,
    phoneCategoryLabel,
    priorityLabel,
    promotionLabel,
} from '../lib/phoneCatalog';
import {
    downPaymentCents,
    ineligibleReason,
    weeklyPaymentCents,
    type IneligibleReason,
    type LevelPlan,
} from '../lib/levelPlans';
import { PLANS_UNREADABLE } from './PhoneFiltersPanel';
import { TIER_LABEL } from '@/shared/patterns/Pills/tierPresentation';
import { STOCK_CLASS } from './PhoneTable';
import { formatStorage } from '@/shared/lib/format/capacity';

export const PHONE_DETAIL_TABS = ['producto', 'cuotas', 'ventas', 'similares'] as const;
export type PhoneDetailTab = (typeof PHONE_DETAIL_TABS)[number];

const TAB_LABEL: Record<PhoneDetailTab, string> = {
    producto: 'Producto',
    cuotas: 'Cuotas por nivel',
    ventas: 'Ventas hoy',
    similares: 'Similares',
};

/** Por qué cada campo enriquecido sale «sin fuente»: el catálogo de venta no lo publica. */
export const MISSING_FIELD_REASON = {
    rating: 'Ningún servicio de Fiado guarda calificación ni reseñas del equipo.',
    highlights: 'El catálogo de venta (`GET /wizard/catalog`) no publica `highlights`.',
    specs: 'El catálogo de venta (`GET /wizard/catalog`) no publica `specs`.',
    warranty: 'El catálogo de venta (`GET /wizard/catalog`) no publica `warrantyMonths`.',
    launch: 'El catálogo de venta (`GET /wizard/catalog`) no publica `launchDate`.',
} as const;

const SECTION = 'mb-3.5';
const SECTION_TITLE =
    'mb-1.5 border-b border-border pb-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground';
const EMPTY_NOTE = 'text-[11px] italic text-muted-foreground';

const INELIGIBLE_LABEL: Record<IneligibleReason, (plan: LevelPlan) => string> = {
    NOT_FINANCEABLE: () => 'SureKeep no lo financia',
    TIER: () => 'Gama fuera del plan',
    CAP: (plan) => `Excede límite (${formatMXN(plan.maxFinancedCents / 100)})`,
};

export interface PhoneSalesState {
    rows: RecentSale[];
    isLoading: boolean;
    error: Error | null;
}

export function PhoneDetailPanel({
    item,
    phones,
    plans,
    sales,
    tab,
    onTabChange,
    onSell,
    onSelectSku,
}: {
    item: CatalogItem | null;
    /** Todos los teléfonos de la tienda: colores del modelo y similares. */
    phones: CatalogItem[];
    plans: LevelPlan[] | null;
    sales: PhoneSalesState;
    tab: PhoneDetailTab;
    onTabChange: (tab: PhoneDetailTab) => void;
    onSell: (sku: string) => void;
    onSelectSku: (sku: string) => void;
}) {
    if (!item) {
        return (
            <MasterDetailEmpty
                icon={<PanelLeft className="size-8 opacity-40" aria-hidden />}
                title="Selecciona un producto para ver su detalle"
            />
        );
    }

    const tone = stockToneOf(item);
    const promo = promotionLabel(item);

    return (
        <>
            <div className="flex-none border-b border-border bg-muted/20 px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <div>
                        <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Producto
                        </div>
                        <div className="text-[14px] font-semibold leading-tight">
                            {productLabel(item)}
                        </div>
                        <div className="font-mono text-[10.5px] text-muted-foreground">
                            {item.sku}
                        </div>
                    </div>
                    {promo === null ? null : (
                        <Badge variant="info" size="xs">
                            {promo}
                        </Badge>
                    )}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-[22px] font-bold tabular-nums">
                        {formatMXN(item.cashPriceCents / 100)}
                    </span>
                    <span className="text-[10.5px] text-muted-foreground">
                        · Stock: {item.stock ?? 0}
                        {tone === 'low' ? ' (bajo)' : ''}
                    </span>
                </div>
            </div>

            <div className="flex flex-none gap-1.5 border-b border-border px-3.5 py-2.5">
                <button
                    type="button"
                    disabled={tone === 'out'}
                    onClick={() => {
                        onSell(item.sku);
                    }}
                    className="inline-flex h-[28px] flex-1 items-center justify-center gap-1.5 rounded-[5px] bg-primary px-2 text-[11.5px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <Plus className="size-3" aria-hidden />
                    Iniciar venta con este producto
                </button>
            </div>

            <Tabs
                wrap
                className="flex-none px-2 py-1"
                activeTab={tab}
                onChange={(key) => {
                    onTabChange(key as PhoneDetailTab);
                }}
                tabs={PHONE_DETAIL_TABS.map((key) => ({ key, label: TAB_LABEL[key] }))}
            />

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-[12px]">
                {tab === 'producto' ? (
                    <ProductTab item={item} phones={phones} plans={plans} />
                ) : tab === 'cuotas' ? (
                    <QuotesTab item={item} plans={plans} />
                ) : tab === 'ventas' ? (
                    <SalesTab item={item} sales={sales} />
                ) : (
                    <SimilarTab item={item} phones={phones} onSelectSku={onSelectSku} />
                )}
            </div>
        </>
    );
}

function Section({ title, children }: { title?: string; children: ReactNode }) {
    return (
        <div className={SECTION}>
            {title === undefined ? null : <h4 className={SECTION_TITLE}>{title}</h4>}
            {children}
        </div>
    );
}

function InfoRow({ label, value }: { label: ReactNode; value: ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-2 py-[3px]">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    );
}

function ProductTab({
    item,
    phones,
    plans,
}: {
    item: CatalogItem;
    phones: CatalogItem[];
    plans: LevelPlan[] | null;
}) {
    const colors = availableColors(item, phones);
    const priority = priorityLabel(item);
    const promo = promotionLabel(item);
    const description = item.description?.trim() ?? '';

    return (
        <>
            <Section>
                {description === '' ? (
                    <p className={EMPTY_NOTE}>Sin descripción</p>
                ) : (
                    <p className="text-[12px] leading-normal text-muted-foreground">
                        {description}
                    </p>
                )}
                <div className="mt-1 flex items-center gap-2 text-[11px]">
                    <Star className="size-3 text-warning" aria-hidden />
                    <NoSource reason={MISSING_FIELD_REASON.rating} />
                </div>
            </Section>

            <Section title="Lo destacado">
                <NoSource reason={MISSING_FIELD_REASON.highlights} className="text-[11px]" />
            </Section>

            <Section title="Especificaciones técnicas">
                <InfoRow
                    label="Almacenamiento"
                    value={formatStorage(item.capacity) || 'Sin dato'}
                />
                {formatStorage(item.ram) !== '' && (
                    <InfoRow label="RAM" value={formatStorage(item.ram)} />
                )}
                <InfoRow
                    label="Procesador, RAM, pantalla, cámara, batería"
                    value={<NoSource reason={MISSING_FIELD_REASON.specs} />}
                />
            </Section>

            <Section title="Colores disponibles">
                {colors.length === 0 ? (
                    <p className={EMPTY_NOTE}>Sin dato</p>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {colors.map((color) => (
                            <Badge
                                key={color}
                                variant="muted"
                                size="xs"
                                className="px-2 py-0.5 text-[10.5px]"
                            >
                                {color}
                            </Badge>
                        ))}
                    </div>
                )}
            </Section>

            <Section title="Datos comerciales">
                <InfoRow label="SKU" value={<span className="font-mono">{item.sku}</span>} />
                <InfoRow label="Marca" value={item.brand || 'Sin dato'} />
                <InfoRow label="Modelo" value={modelLabel(item) || 'Sin dato'} />
                <InfoRow label="Precio contado" value={formatMXN(item.cashPriceCents / 100)} />
                <InfoRow
                    label="Stock actual"
                    value={
                        <span className={STOCK_CLASS[stockToneOf(item)]}>{stockLabelOf(item)}</span>
                    }
                />
                <InfoRow label="Categoría" value={phoneCategoryLabel(item)} />
                <InfoRow label="Prioridad de venta" value={priority ?? 'Sin dato'} />
                <InfoRow
                    label="Garantía"
                    value={<NoSource reason={MISSING_FIELD_REASON.warranty} />}
                />
                <InfoRow
                    label="Lanzamiento"
                    value={<NoSource reason={MISSING_FIELD_REASON.launch} />}
                />
            </Section>

            <Section title="Elegibilidad crédito SureKeep">
                {plans === null ? (
                    <NoSource reason={PLANS_UNREADABLE} className="text-[11px]" />
                ) : (
                    plans.map((plan) => {
                        const reason = ineligibleReason(item, plan);
                        return (
                            <InfoRow
                                key={plan.level}
                                label={`Nivel ${TIER_LABEL[ClientLevelEnum[plan.level]]}`}
                                value={
                                    reason === null ? (
                                        <span className="text-success">Elegible</span>
                                    ) : (
                                        <span className="text-danger">
                                            {INELIGIBLE_LABEL[reason](plan)}
                                        </span>
                                    )
                                }
                            />
                        );
                    })
                )}
            </Section>

            <Section title="Financieras que aceptan este producto">
                {item.eligibleFinanciers.length === 0 ? (
                    <p className={EMPTY_NOTE}>Sin dato</p>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {financierChips(item.eligibleFinanciers).map((chip) => (
                            <Badge
                                key={chip.code}
                                variant={chip.tone}
                                size="xs"
                                className="text-[10px]"
                            >
                                {chip.name}
                            </Badge>
                        ))}
                    </div>
                )}
            </Section>

            {promo === null ? null : (
                <Section title="Promoción vigente">
                    <div className="rounded-md bg-primary/[0.08] p-2 text-[11.5px] text-primary">
                        {promo}
                    </div>
                </Section>
            )}
        </>
    );
}

function QuotesTab({ item, plans }: { item: CatalogItem; plans: LevelPlan[] | null }) {
    return (
        <>
            <Section title="Cotización semanal por nivel (SureKeep)">
                <div className="mb-2 text-[10.5px] text-muted-foreground">
                    Enganche del nivel + plazo estándar + TNA del nivel. IVA 16% sobre intereses.
                </div>
                {plans === null ? (
                    <NoSource reason={PLANS_UNREADABLE} className="text-[11px]" />
                ) : (
                    <table className="w-full border-collapse text-[11px]">
                        <thead>
                            <tr>
                                {['Nivel', 'Enganche', 'Plazo', 'TNA', 'Cuota/sem'].map((head) => (
                                    <th
                                        key={head}
                                        scope="col"
                                        className="bg-muted/40 px-2 py-[5px] text-center text-[9.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground"
                                    >
                                        {head}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((plan) => {
                                const payment = weeklyPaymentCents(item, plan);
                                return (
                                    <tr key={plan.level} className="border-t border-border/50">
                                        <td className="px-2 py-[5px] text-center">
                                            <TierPill level={ClientLevelEnum[plan.level]} />
                                        </td>
                                        <td className="px-2 py-[5px] text-center">
                                            {formatMXN(downPaymentCents(item, plan) / 100)} (
                                            {Math.round(plan.minDownPaymentPct * 100)}%)
                                        </td>
                                        <td className="px-2 py-[5px] text-center">
                                            {plan.termWeeks} sem
                                        </td>
                                        <td className="px-2 py-[5px] text-center">
                                            {Math.round(plan.tna * 100)}%
                                        </td>
                                        <td className="px-2 py-[5px] text-right tabular-nums">
                                            {payment === null ? (
                                                <span className="text-danger">No elegible</span>
                                            ) : (
                                                formatMXN(payment / 100)
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Section>
            <div className="rounded-md bg-info-soft p-2 text-[11px] leading-relaxed text-info">
                Solo simula. El motor real calcula al arrancar la solicitud en el paso 06 del
                wizard.
            </div>
        </>
    );
}

function SalesTab({ item, sales }: { item: CatalogItem; sales: PhoneSalesState }) {
    if (sales.isLoading) return <p className={EMPTY_NOTE}>Cargando…</p>;
    if (sales.error) {
        return <NoSource reason="No se pudieron leer tus ventas." className="text-[11px]" />;
    }
    const facts = skuSalesFacts(item.sku, sales.rows);
    return (
        <>
            <Section title="Ventas hoy de este SKU">
                {facts.today.length === 0 ? (
                    <p className={EMPTY_NOTE}>Sin ventas hoy de este SKU.</p>
                ) : (
                    facts.today.map((sale) => (
                        <InfoRow
                            key={sale.saleId}
                            label={formatTimeMx(sale.soldAt)}
                            value={`${sale.customerName || 'Cliente sin nombre'} · ${formatMXN(skuSoldCents(sale, item.sku) / 100)}`}
                        />
                    ))
                )}
            </Section>
            <Section title={`Ventas ${String(RECENT_WINDOW_DAYS)} días`}>
                {facts.recent.length === 0 ? (
                    <p className={EMPTY_NOTE}>Sin ventas recientes de este SKU.</p>
                ) : (
                    <>
                        <div className="text-[12px]">
                            {facts.recent.length} {facts.recent.length === 1 ? 'venta' : 'ventas'}{' '}
                            en los últimos {RECENT_WINDOW_DAYS} días.
                        </div>
                        {facts.recentVolumeCents === null ? null : (
                            <div className="mt-1 text-[10.5px] text-muted-foreground">
                                Volumen: {formatMXN(facts.recentVolumeCents / 100)}
                            </div>
                        )}
                    </>
                )}
            </Section>
            <p className={cn(EMPTY_NOTE, 'not-italic')}>{OWN_SALES_ONLY_NOTE}</p>
        </>
    );
}

function SimilarTab({
    item,
    phones,
    onSelectSku,
}: {
    item: CatalogItem;
    phones: CatalogItem[];
    onSelectSku: (sku: string) => void;
}) {
    const similar = similarProducts(item, phones);
    return (
        <Section title="Productos comparables · misma banda de precio">
            {similar.length === 0 ? (
                <p className={EMPTY_NOTE}>Sin productos comparables en la banda de precio.</p>
            ) : (
                similar.map((candidate) => (
                    <button
                        key={candidate.sku}
                        type="button"
                        onClick={() => {
                            onSelectSku(candidate.sku);
                        }}
                        className="flex w-full items-baseline justify-between gap-2 rounded px-1 py-[3px] text-left hover:bg-accent/40"
                    >
                        <span className="truncate text-muted-foreground">
                            {productLabel(candidate)}
                            <span className="ml-1 font-mono text-[9.5px]">{candidate.sku}</span>
                        </span>
                        <span className="whitespace-nowrap font-medium">
                            {formatMXN(candidate.cashPriceCents / 100)}
                        </span>
                    </button>
                ))
            )}
        </Section>
    );
}
